import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, XCircle, Palette, KeyRound, Mail, CreditCard, Tag, Plus, Trash2, Inbox, Phone, Sparkles } from "lucide-react";
import AdminAICostDashboard from "@/components/AdminAICostDashboard";

function Stat({ label, value }) {
    return (
        <div className="border border-border bg-white p-6">
            <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">{label}</div>
            <div className="font-display font-black text-2xl sm:text-4xl mt-2">{value}</div>
        </div>
    );
}

function BrandingCard() {
    const branding = useBranding();
    const [form, setForm] = useState({
        product_name: "", short_name: "", logo_url: "", primary_color: "#002FA7", tagline: "", support_email: ""
    });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setForm({
            product_name: branding.product_name || "",
            short_name: branding.short_name || "",
            logo_url: branding.logo_url || "",
            primary_color: branding.primary_color || "#002FA7",
            tagline: branding.tagline || "",
            support_email: branding.support_email || "",
        });
    }, [branding.product_name, branding.short_name, branding.logo_url, branding.primary_color, branding.tagline, branding.support_email]);

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.patch("/settings/branding", form);
            await branding.refresh();
            toast.success("Branding updated");
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    return (
        <form onSubmit={save} className="border border-border bg-white p-4 sm:p-8 space-y-5">
            <div className="flex items-center gap-2">
                <Palette className="w-5 h-5 text-ikb" />
                <h2 className="font-display font-bold text-xl">Platform branding</h2>
            </div>
            <p className="text-sm text-muted-foreground -mt-2">These values are reflected everywhere — landing page, app shell, emails, page titles.</p>
            <div className="grid md:grid-cols-2 gap-5">
                <div>
                    <Label className="text-xs uppercase tracking-widest font-bold">Product name</Label>
                    <Input value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} className="rounded-none mt-2 h-11" data-testid="branding-product-name" />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-widest font-bold">Short name</Label>
                    <Input value={form.short_name} onChange={(e) => setForm({ ...form, short_name: e.target.value })} className="rounded-none mt-2 h-11" data-testid="branding-short-name" />
                </div>
                <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-widest font-bold">Logo URL (PNG/SVG)</Label>
                    <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://..." className="rounded-none mt-2 h-11" data-testid="branding-logo-url" />
                    {form.logo_url && <img src={form.logo_url} alt="logo preview" className="h-8 mt-3" />}
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-widest font-bold">Primary color</Label>
                    <div className="flex gap-2 mt-2">
                        <Input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="rounded-none h-11 w-20" data-testid="branding-primary-color" />
                        <Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="rounded-none h-11 font-mono" />
                    </div>
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-widest font-bold">Support email</Label>
                    <Input type="email" value={form.support_email} onChange={(e) => setForm({ ...form, support_email: e.target.value })} className="rounded-none mt-2 h-11" data-testid="branding-support-email" />
                </div>
                <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-widest font-bold">Landing tagline</Label>
                    <Textarea rows={2} value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="rounded-none mt-2" data-testid="branding-tagline" />
                </div>
            </div>
            <Button type="submit" disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="branding-save">{busy ? "Saving..." : "Save branding"}</Button>
        </form>
    );
}

function IntegrationsCard() {
    const [data, setData] = useState(null);
    const [form, setForm] = useState({
        resend_api_key: "", resend_sender_email: "",
        cashfree_app_id: "", cashfree_secret_key: "",
        cashfree_environment: "TEST", cashfree_webhook_secret: "",
        google_maps_api_key: "",
    });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/settings/integrations");
            setData(data);
            setForm((f) => ({
                ...f,
                resend_sender_email: data.resend_sender_email || "",
                cashfree_app_id: data.cashfree_app_id || "",
                cashfree_environment: data.cashfree_environment || "TEST",
            }));
        } catch (err) { toast.error(formatApiError(err)); }
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const patch = {};
            for (const [k, v] of Object.entries(form)) {
                if (v !== "" && v !== null && v !== undefined) patch[k] = v;
            }
            await api.patch("/settings/integrations", patch);
            toast.success("API keys saved");
            setForm({ ...form, resend_api_key: "", cashfree_secret_key: "", cashfree_webhook_secret: "", google_maps_api_key: "" });
            await load();
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    if (!data) return null;

    return (
        <form onSubmit={save} className="border border-border bg-white p-4 sm:p-8 space-y-8">
            <div className="flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-ikb" />
                <h2 className="font-display font-bold text-xl">API keys & integrations</h2>
            </div>
            <p className="text-sm text-muted-foreground -mt-4">Keys are stored encrypted-at-rest and only shown masked here. Leave a field blank to keep the existing value.</p>

            <section className="space-y-4">
                <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <h3 className="font-display font-bold">Resend (transactional email)</h3>
                    <StatusPill ok={data.resend_configured} label={data.resend_configured ? "Configured" : "Not configured"} />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">API Key {data.resend_api_key_masked && <span className="ml-2 font-mono text-[10px] text-muted-foreground">current: {data.resend_api_key_masked}</span>}</Label>
                        <Input type="password" autoComplete="off" placeholder={data.resend_configured ? "•••• keep current" : "re_..."} value={form.resend_api_key} onChange={(e) => setForm({ ...form, resend_api_key: e.target.value })} className="rounded-none mt-2 h-11 font-mono" data-testid="resend-api-key" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Sender email</Label>
                        <Input type="email" placeholder="hello@yourdomain.com" value={form.resend_sender_email} onChange={(e) => setForm({ ...form, resend_sender_email: e.target.value })} className="rounded-none mt-2 h-11" data-testid="resend-sender" />
                    </div>
                </div>
                <p className="text-xs text-muted-foreground">Get a free key at resend.com. Used for KYC approval, recruiter invite, interview-scheduled emails.</p>
            </section>

            <section className="space-y-4 pt-6 border-t border-border">
                <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-muted-foreground" />
                    <h3 className="font-display font-bold">Cashfree (billing)</h3>
                    <StatusPill ok={data.cashfree_configured} label={data.cashfree_configured ? `Configured · ${data.cashfree_environment}` : "Not configured"} />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">App ID</Label>
                        <Input value={form.cashfree_app_id} onChange={(e) => setForm({ ...form, cashfree_app_id: e.target.value })} className="rounded-none mt-2 h-11 font-mono" data-testid="cashfree-app-id" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Environment</Label>
                        <select value={form.cashfree_environment} onChange={(e) => setForm({ ...form, cashfree_environment: e.target.value })} className="mt-2 h-11 w-full px-3 border border-border bg-white text-sm" data-testid="cashfree-env">
                            <option value="TEST">TEST (sandbox)</option>
                            <option value="PROD">PROD (live)</option>
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Secret key {data.cashfree_secret_key_masked && <span className="ml-2 font-mono text-[10px] text-muted-foreground">current: {data.cashfree_secret_key_masked}</span>}</Label>
                        <Input type="password" autoComplete="off" placeholder={data.cashfree_configured ? "•••• keep current" : "cfsk_..."} value={form.cashfree_secret_key} onChange={(e) => setForm({ ...form, cashfree_secret_key: e.target.value })} className="rounded-none mt-2 h-11 font-mono" data-testid="cashfree-secret" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Webhook secret {data.cashfree_webhook_secret_masked && <span className="ml-2 font-mono text-[10px] text-muted-foreground">current: {data.cashfree_webhook_secret_masked}</span>}</Label>
                        <Input type="password" autoComplete="off" value={form.cashfree_webhook_secret} onChange={(e) => setForm({ ...form, cashfree_webhook_secret: e.target.value })} className="rounded-none mt-2 h-11 font-mono" data-testid="cashfree-webhook" />
                    </div>
                </div>
                <p className="text-xs text-muted-foreground">Cashfree checkout flow (per-job-post, per-seat subscription, candidate-DB credits) ships next iteration. Save keys now to enable it instantly when it lands.</p>
            </section>

            <section className="space-y-4 pt-6 border-t border-border">
                <div className="flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-muted-foreground" />
                    <h3 className="font-display font-bold">Google Maps Places (city autocomplete)</h3>
                    <StatusPill ok={data.google_maps_configured} label={data.google_maps_configured ? "Configured" : "Not configured"} />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <Label className="text-xs uppercase tracking-widest font-bold">API Key {data.google_maps_api_key_masked && <span className="ml-2 font-mono text-[10px] text-muted-foreground">current: {data.google_maps_api_key_masked}</span>}</Label>
                        <Input type="password" autoComplete="off" placeholder={data.google_maps_configured ? "•••• keep current" : "AIza..."} value={form.google_maps_api_key} onChange={(e) => setForm({ ...form, google_maps_api_key: e.target.value })} className="rounded-none mt-2 h-11 font-mono" data-testid="gmaps-api-key" />
                    </div>
                </div>
                <p className="text-xs text-muted-foreground">Used for city-autocomplete on the public Apply form (and recruiter forms). Enable the <span className="font-mono">Places API</span> in Google Cloud, then restrict the key by HTTP referrer to your domains. Without a key the city field falls back to plain text.</p>
            </section>

            <Button type="submit" disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="integrations-save">{busy ? "Saving..." : "Save keys"}</Button>
        </form>
    );
}

function StatusPill({ ok, label }) {
    return (
        <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
            <span className={`w-1.5 h-1.5 ${ok ? "bg-emerald-500" : "bg-amber-500"}`} />
            {label}
        </span>
    );
}


function EmailTemplatesCard() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = async () => {
        try { const { data } = await api.get("/settings/email-templates"); setItems(data || []); }
        catch (err) { toast.error(formatApiError(err)); }
    };
    useEffect(() => { load(); }, []);
    const save = async (key, patch) => {
        setBusy(true);
        try { await api.patch(`/settings/email-templates/${key}`, patch); toast.success("Saved"); await load(); setOpen(null); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    return (
        <section className="border border-border bg-white p-6 space-y-4" data-testid="admin-email-templates">
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-display font-bold text-2xl">Email templates</h2>
                    <p className="text-sm text-foreground/80 mt-1">Edit the subject and body of system emails. Placeholders like {"{candidate_name}"} are auto-filled.</p>
                </div>
            </div>
            <div className="border border-border divide-y divide-border">
                {items.map((t) => (
                    <div key={t.key} className="p-4 flex items-start justify-between gap-3 flex-wrap" data-testid={`tpl-${t.key}`}>
                        <div className="min-w-0 flex-1">
                            <div className="font-medium text-sm">{t.name}</div>
                            <div className="text-xs font-mono text-foreground/80 mt-0.5 truncate">{t.subject}</div>
                        </div>
                        <button onClick={() => setOpen(t)} className="text-xs font-mono uppercase tracking-widest border border-border px-3 py-1.5 hover:bg-secondary" data-testid={`tpl-edit-${t.key}`}>Edit</button>
                    </div>
                ))}
            </div>
            {open && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setOpen(null)}>
                    <div className="bg-white border border-border w-full max-w-2xl p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-display font-bold text-xl">{open.name}</h3>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Subject</Label>
                            <Input value={open.subject} onChange={(e) => setOpen({ ...open, subject: e.target.value })} className="rounded-none h-10 mt-2" data-testid="tpl-subject" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Body (HTML)</Label>
                            <Textarea value={open.body} onChange={(e) => setOpen({ ...open, body: e.target.value })} className="rounded-none mt-2 min-h-[200px] font-mono text-xs" data-testid="tpl-body" />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" className="rounded-none" onClick={() => setOpen(null)}>Cancel</Button>
                            <Button onClick={() => save(open.key, { subject: open.subject, body: open.body })} disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="tpl-save">{busy ? "Saving…" : "Save"}</Button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

function RevenueCard() {
    const [data, setData] = useState(null);
    useEffect(() => {
        (async () => {
            try { const { data } = await api.get("/admin/revenue"); setData(data); }
            catch { /* ignore */ }
        })();
    }, []);
    if (!data) return null;
    return (
        <section className="border border-border bg-white p-6 space-y-4" data-testid="admin-revenue">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display font-bold text-2xl">Revenue · {data.window_days}d</h2>
                    <p className="text-sm text-foreground/80 mt-1">Cashfree-paid orders over the window.</p>
                </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
                <Stat label="Total revenue" value={"₹" + Number(data.total_inr || 0).toLocaleString("en-IN")} />
                <Stat label="Paying companies" value={data.paying_companies ?? 0} />
                {Object.entries(data.by_plan || {}).map(([plan, amt]) => (
                    <Stat key={plan} label={plan + " revenue"} value={"₹" + Number(amt || 0).toLocaleString("en-IN")} />
                ))}
            </div>
        </section>
    );
}


function EnquiriesCard() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("new");

    const load = async () => {
        try {
            const { data } = await api.get("/admin/enquiries", { params: filter ? { status: filter } : {} });
            setItems(data?.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/admin/enquiries/${id}`, { status });
            toast.success("Updated");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <section data-testid="admin-enquiries" className="border border-border bg-white">
            <div className="border-b border-border px-5 py-3 flex items-center justify-between bg-secondary/40">
                <h2 className="font-display font-bold text-sm uppercase tracking-widest flex items-center gap-2">
                    <Inbox className="w-4 h-4 text-ikb" /> Hiring enquiries
                </h2>
                <div className="flex items-center gap-1">
                    {["new", "contacted", "resolved", ""].map((s) => (
                        <button
                            key={s || "all"}
                            onClick={() => setFilter(s)}
                            className={`text-[10px] font-mono uppercase tracking-widest border border-border px-2 py-1 ${filter === s ? "bg-ikb text-white" : "bg-white hover:bg-secondary"}`}
                            data-testid={`enq-filter-${s || "all"}`}
                        >
                            {s || "all"}
                        </button>
                    ))}
                </div>
            </div>
            {items.length === 0 ? (
                <div className="p-8 text-center text-xs font-mono text-foreground/60">No {filter || "any"} enquiries.</div>
            ) : (
                <div className="divide-y divide-border max-h-[480px] overflow-y-auto">
                    {items.map((e) => (
                        <div key={e.id} className="p-4 flex items-start gap-4 flex-wrap" data-testid={`enq-row-${e.id}`}>
                            <div className="flex-1 min-w-[260px]">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-sm">{e.name}</span>
                                    {e.company && <span className="text-xs text-foreground/70">· {e.company}</span>}
                                    <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 ${e.status === "new" ? "bg-rose-100 text-rose-700" : e.status === "contacted" ? "bg-amber-100 text-amber-700" : e.status === "resolved" ? "bg-emerald-100 text-emerald-700" : "bg-secondary"}`}>{e.status}</span>
                                </div>
                                <div className="flex items-center gap-3 text-xs font-mono text-foreground/70 mt-1 flex-wrap">
                                    <a href={`mailto:${e.email}`} className="hover:text-ikb"><Mail className="w-3 h-3 inline mr-1" />{e.email}</a>
                                    {e.phone && <a href={`tel:${e.phone}`} className="hover:text-ikb"><Phone className="w-3 h-3 inline mr-1" />{e.phone}</a>}
                                </div>
                                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-foreground/60 mt-1 flex-wrap">
                                    <span>{e.roles_count}</span><span>·</span>
                                    <span>{e.role_type}</span><span>·</span>
                                    <span>{e.timeline}</span><span>·</span>
                                    <span>{new Date(e.created_at).toLocaleString()}</span>
                                </div>
                                {e.message && <p className="text-sm mt-2 text-foreground/80 whitespace-pre-wrap">{e.message}</p>}
                            </div>
                            <div className="flex items-center gap-1.5">
                                {e.status !== "contacted" && <button onClick={() => setStatus(e.id, "contacted")} className="text-[10px] font-mono uppercase tracking-widest border border-amber-300 bg-amber-50 text-amber-700 px-2 py-1 hover:bg-amber-100" data-testid={`enq-contacted-${e.id}`}>Mark contacted</button>}
                                {e.status !== "resolved" && <button onClick={() => setStatus(e.id, "resolved")} className="text-[10px] font-mono uppercase tracking-widest border border-emerald-300 bg-emerald-50 text-emerald-700 px-2 py-1 hover:bg-emerald-100" data-testid={`enq-resolve-${e.id}`}>Resolve</button>}
                                {e.status !== "spam" && <button onClick={() => setStatus(e.id, "spam")} className="text-[10px] font-mono uppercase tracking-widest border border-rose-300 bg-rose-50 text-rose-700 px-2 py-1 hover:bg-rose-100" data-testid={`enq-spam-${e.id}`}>Spam</button>}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}


function DiscountCodesCard() {
    const [codes, setCodes] = useState([]);
    const [form, setForm] = useState({ code: "", percent_off: 10, max_uses: "", valid_until: "", description: "", applies_to: "" });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/discount-codes");
            setCodes(data?.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!form.code.trim()) { toast.error("Code is required"); return; }
        setBusy(true);
        try {
            await api.post("/discount-codes", {
                code: form.code.trim(),
                percent_off: Number(form.percent_off),
                max_uses: form.max_uses ? Number(form.max_uses) : null,
                valid_until: form.valid_until || null,
                description: form.description || "",
                applies_to: form.applies_to ? form.applies_to.split(",").map((s) => s.trim()).filter(Boolean) : [],
            });
            toast.success("Discount code created");
            setForm({ code: "", percent_off: 10, max_uses: "", valid_until: "", description: "", applies_to: "" });
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const archive = async (id) => {
        if (!confirm("Archive this discount code?")) return;
        try {
            await api.delete(`/discount-codes/${id}`);
            toast.success("Code archived");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <section data-testid="admin-discount-codes" className="border border-border bg-white">
            <div className="border-b border-border px-5 py-3 flex items-center justify-between bg-secondary/40">
                <h2 className="font-display font-bold text-sm uppercase tracking-widest flex items-center gap-2">
                    <Tag className="w-4 h-4 text-amber-500" /> Discount codes
                </h2>
                <span className="text-[10px] font-mono text-foreground/60">{codes.length} codes</span>
            </div>
            <div className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2 border-b border-border bg-secondary/20">
                <Input placeholder="CODE (eg LAUNCH50)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-none uppercase font-mono" data-testid="dc-code" />
                <Input type="number" placeholder="% off" value={form.percent_off} onChange={(e) => setForm({ ...form, percent_off: e.target.value })} className="rounded-none" data-testid="dc-percent" />
                <Input type="number" placeholder="Max uses" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className="rounded-none" data-testid="dc-max" />
                <Input type="date" placeholder="Expires" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="rounded-none" data-testid="dc-expires" />
                <Input placeholder="Applies to (eg pro,starter)" value={form.applies_to} onChange={(e) => setForm({ ...form, applies_to: e.target.value })} className="rounded-none" data-testid="dc-applies" />
                <Button onClick={create} disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="dc-create"><Plus className="w-4 h-4 mr-1" /> Add</Button>
            </div>
            <table className="w-full text-sm">
                <thead className="bg-secondary/30 text-[10px] font-mono uppercase tracking-widest text-foreground/70">
                    <tr>
                        <th className="text-left px-5 py-2">Code</th>
                        <th className="text-left px-3 py-2">% off</th>
                        <th className="text-left px-3 py-2">Uses</th>
                        <th className="text-left px-3 py-2">Expires</th>
                        <th className="text-left px-3 py-2">Applies to</th>
                        <th className="text-left px-3 py-2">Status</th>
                        <th className="px-5 py-2"></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {codes.length === 0 ? (
                        <tr><td colSpan={7} className="px-5 py-8 text-center text-xs font-mono text-foreground/60">No discount codes yet.</td></tr>
                    ) : codes.map((c) => (
                        <tr key={c.id} data-testid={`dc-row-${c.code}`} className="hover:bg-secondary/30">
                            <td className="px-5 py-2 font-mono font-bold">{c.code}</td>
                            <td className="px-3 py-2">{c.percent_off}%</td>
                            <td className="px-3 py-2 text-xs">{c.used_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                            <td className="px-3 py-2 text-xs">{c.valid_until || "—"}</td>
                            <td className="px-3 py-2 text-xs">{(c.applies_to || []).length ? c.applies_to.join(", ") : "all paid"}</td>
                            <td className="px-3 py-2">
                                <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 ${c.archived ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
                                    {c.archived ? "archived" : "active"}
                                </span>
                            </td>
                            <td className="px-5 py-2 text-right">
                                {!c.archived && (
                                    <button onClick={() => archive(c.id)} className="text-xs text-rose-700 hover:underline inline-flex items-center gap-1" data-testid={`dc-archive-${c.code}`}>
                                        <Trash2 className="w-3 h-3" /> Archive
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </section>
    );
}


function SystemHealthCard() {
    const [health, setHealth] = useState(null);
    const [queue, setQueue] = useState(null);
    const [restarting, setRestarting] = useState(false);
    const [lastChecked, setLastChecked] = useState("");

    const refresh = async () => {
        try {
            const [h, q] = await Promise.allSettled([
                api.get("/health"),
                api.get("/admin/processing-queue"),
            ]);
            setHealth(h.status === "fulfilled" ? h.value.data : { status: "down", db: "unreachable" });
            setQueue(q.status === "fulfilled" ? q.value.data : null);
            setLastChecked(new Date().toLocaleTimeString());
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    useEffect(() => {
        refresh();
        const id = setInterval(refresh, 15000);
        return () => clearInterval(id);
    }, []);

    const doRestart = async () => {
        if (!window.confirm("Restart the backend now? Service will be unavailable for ~15-20 seconds. Any in-flight uploads will be lost.")) return;
        setRestarting(true);
        try {
            await api.post("/admin/restart");
            toast.success("Restart signal sent — service will be back in 15-20 seconds");
            setTimeout(refresh, 20000);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setTimeout(() => setRestarting(false), 20000);
        }
    };

    const healthy = health?.status === "ok";
    return (
        <section className="border border-border bg-white p-4 sm:p-8 space-y-5" data-testid="admin-system-health">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <ShieldCheck className={`w-5 h-5 ${healthy ? "text-emerald-600" : "text-rose-600"}`} />
                    <h2 className="font-display font-bold text-xl">System health</h2>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    Auto-refresh 15s {lastChecked && `· last ${lastChecked}`}
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className={`border p-4 ${healthy ? "border-emerald-300 bg-emerald-50" : "border-rose-300 bg-rose-50"}`} data-testid="health-status">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Backend</div>
                    <div className={`text-2xl font-display font-black mt-1 ${healthy ? "text-emerald-700" : "text-rose-700"}`}>
                        {healthy ? "Healthy" : "Down"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 font-mono">db: {health?.db || "—"}</div>
                </div>
                <div className="border border-border p-4" data-testid="health-queue">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Resume queue</div>
                    <div className="text-2xl font-display font-black mt-1">{queue?.in_flight ?? "—"}</div>
                    <div className="text-xs text-muted-foreground mt-1 font-mono">
                        {queue ? `${queue.concurrency} at a time` : "loading"}
                    </div>
                </div>
                <div className="border border-border p-4 flex flex-col justify-between">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Action</div>
                        <div className="text-xs text-foreground/70 mt-1">Use when backend hangs or health is Down.</div>
                    </div>
                    <Button
                        onClick={doRestart}
                        disabled={restarting}
                        className="rounded-none mt-3 bg-rose-600 hover:bg-rose-700 text-white"
                        data-testid="admin-restart-btn"
                    >
                        {restarting ? "Restarting…" : "Restart backend"}
                    </Button>
                </div>
            </div>

            <div className="text-[11px] font-mono text-muted-foreground border-t border-border pt-3">
                Emergency rescue (when normal admin login fails): visit <a href="/rescue" target="_blank" rel="noreferrer" className="underline text-ikb font-bold">/rescue</a> — uses env-based credentials, no DB needed.
                Full monitoring guide: <code className="bg-secondary px-1.5 py-0.5">/app/memory/MONITORING_GUIDE.md</code>
            </div>
        </section>
    );
}


export default function AdminPanel() {
    const [stats, setStats] = useState({});
    const [companies, setCompanies] = useState([]);
    const [filter, setFilter] = useState("pending");

    const load = async () => {
        const [s, c] = await Promise.all([
            api.get("/admin/stats"),
            api.get("/admin/companies", { params: filter ? { status: filter } : {} }),
        ]);
        setStats(s.data);
        setCompanies(c.data || []);
    };

    useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); /* eslint-disable-next-line */ }, [filter]);

    const approve = async (id) => {
        try { await api.post(`/admin/companies/${id}/approve`); toast.success("Approved"); await load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };
    const reject = async (id) => {
        try { await api.post(`/admin/companies/${id}/reject`); toast.success("Rejected"); await load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-10">
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb">Admin</div>
                    <h1 className="font-display font-black text-2xl sm:text-4xl mt-2">Platform overview</h1>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Link to="/admin/spotlight-funnel" data-testid="admin-funnel-link">
                        <button className="rounded-none border border-amber-500 bg-white text-amber-800 hover:bg-amber-50 px-4 h-10 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1">
                            <Sparkles className="w-3 h-3" /> Featured funnel →
                        </button>
                    </Link>
                    <Link to="/admin/landing-content" data-testid="admin-landing-content-link">
                        <button className="rounded-none border border-foreground bg-white hover:bg-foreground hover:text-background px-4 h-10 text-xs font-mono uppercase tracking-widest">
                            Edit landing page →
                        </button>
                    </Link>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-border">
                <Stat label="Companies" value={stats.companies ?? 0} />
                <Stat label="Pending KYC" value={stats.pending_kyc ?? 0} />
                <Stat label="Users" value={stats.users ?? 0} />
                <Stat label="Jobs" value={stats.jobs ?? 0} />
                <Stat label="Candidates" value={stats.candidates ?? 0} />
                <Stat label="Global pool" value={stats.global_candidates ?? 0} />
            </div>

            <BrandingCard />
            <SystemHealthCard />
            <AdminAICostDashboard />
            <IntegrationsCard />
            <EnquiriesCard />
            <DiscountCodesCard />
            <EmailTemplatesCard />
            <RevenueCard />

            <section>
                <div className="flex items-end justify-between mb-4">
                    <h2 className="font-display font-bold text-2xl">KYC requests</h2>
                    <div className="flex items-center gap-2">
                        {["pending", "approved", "rejected", ""].map((s) => (
                            <button
                                key={s || "all"}
                                onClick={() => setFilter(s)}
                                data-testid={`admin-filter-${s || "all"}`}
                                className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 border ${filter === s ? "bg-foreground text-background border-foreground" : "bg-white border-border hover:bg-secondary"}`}
                            >
                                {s || "All"}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="border border-border bg-white divide-y divide-border">
                    {companies.length === 0 ? (
                        <div className="p-8 text-center text-sm text-muted-foreground">No companies in this view</div>
                    ) : companies.map((c) => (
                        <div key={c.id} className="flex items-center justify-between p-5 gap-4 flex-wrap">
                            <div>
                                <div className="font-display font-bold text-lg">{c.name}</div>
                                <div className="text-xs font-mono text-muted-foreground mt-0.5">
                                    {c.website || "no website"} · {c.industry || "—"} · {c.headquarters || "—"}
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${
                                    c.kyc_status === "approved" ? "border-emerald-200 bg-emerald-50 text-emerald-700" :
                                    c.kyc_status === "rejected" ? "border-red-200 bg-red-50 text-red-700" :
                                    "border-amber-200 bg-amber-50 text-amber-700"
                                }`}>
                                    {c.kyc_status === "approved" ? <ShieldCheck className="w-3 h-3" /> :
                                     c.kyc_status === "rejected" ? <XCircle className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
                                    {c.kyc_status}
                                </span>
                                {c.kyc_status !== "approved" && (
                                    <Button onClick={() => approve(c.id)} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-9" data-testid={`approve-${c.id}`}>Approve</Button>
                                )}
                                {c.kyc_status !== "rejected" && (
                                    <Button onClick={() => reject(c.id)} variant="outline" className="rounded-none h-9" data-testid={`reject-${c.id}`}>Reject</Button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
